// Skeletonization: a command string reduced to its STRUCTURE, so that two
// instances of the same behaviour collide on one key.
//
// This is the unit swap the whole package turns on. Discovery used to rank by
// binary NAME, which works while the name is the behaviour (`cdb.exe`,
// `analyzeHeadless`, a house CLI) and collapses the moment work moves into
// Bash and Python, where `python3` and `bash -c` carry no signal at all. The
// behaviour then lives in the shape of the program, not in what it is called.
//
// Two normalizers, because the two languages hide structure in different
// places:
//
//   SHELL — structure is the program names, the flag NAMES, the operators and
//   the pipe topology. Values are noise: paths, numbers, hex, quoted strings
//   and flag arguments all become typed placeholders. `cdb -z D:\a.dmp -c "…"`
//   and `cdb -z E:\b.dmp -c "…"` are one behaviour.
//
//   EMBEDDED SCRIPT (python -c, heredocs, node -e) — the tokens are almost
//   entirely incidental. What identifies the behaviour is the API SURFACE it
//   touches: which modules it imports and which functions it calls. Two scripts
//   that import pandas and call read_csv/groupby are the same behaviour even
//   with no tokens in common. Skeletonizing those by token would produce one
//   singleton per script and report "no structure here", which is a conclusion
//   about the normalizer rather than about the corpus.
//
// Everything is pure and deterministic — same input, same key, forever, which
// is required for cluster identity to be stable across runs.

import { resolveShellVars, splitStatements, tokenize } from "./shell.js";

// Statements that are SCAFFOLDING: they position the real work rather than
// being it. Stripping them keeps one behaviour from fragmenting into a cluster
// per working directory. This list is mechanical — a fixed set of shell
// housekeeping verbs — and is deliberately NOT a list of uninteresting
// behaviours. Deciding that `cargo test` is not worth a lens is judgment and
// belongs to the judge; deciding that `cd` is not the point of a command is
// not.
const SCAFFOLD_VERBS = new Set([
  "cd", "pushd", "popd", "mkdir", "export", "set", "unset", "source", ".",
  "echo", "printf", "true", "false", "which", "command", "type", "exit",
  "sleep", "wait", "eval", "trap", "umask", "shift", "read",
]);

// Verbs and operators with a consequence outside the sandbox. Used to grade
// candidates, never to gate them: a read-only behaviour can still be worth a
// lens, it just has a weaker claim than one that changes the world.
//
// `mutates` IS TRI-STATE: true / false / null, where null means the program was
// recognised but its consequence is not decidable from the command. Two prior
// versions of this were boolean and both were wrong in the same direction —
// `false` is a claim ("read-only") exactly as much as `true` is, so a boolean
// forces a guess on every command and the guess reaches the judge as evidence.
//
// The first version called `sed` destructive; `sed -n '10,20p' f` is the most
// common way to read a line range, and the flag fed a false consequence straight
// into the judge's ranking. In-place editing is `sed -i`, caught below.
//
// The second version kept a flat program-NAME list that mixed two kinds of
// program, and the mistake is only visible on real output: measured over one
// window's candidates, EVERY cluster came back mutating, `kubectl get pods` and
// `cargo test` among them. A flag that every row carries distinguishes nothing.
//
//   SINGLE-PURPOSE — the name IS the act. `rm`, `mv`, `chmod`. Name-matching is
//     correct here and stays.
//   SUBCOMMAND-BEARING — `git`, `kubectl`, `docker`, `cargo`. The verb after the
//     program decides, and `git log` / `kubectl get` / `cargo test` are reads.
//     These resolve through SUBCOMMANDS below.
//
// The subcommand tables are deliberately NOT exhaustive. They carry the verbs
// whose consequence is unambiguous; anything else returns null rather than a
// guess, because the judge already sees the raw command and can read the verb
// itself. Adding a verb here is cheap — inventing a rule for `docker exec`,
// whose consequence depends on the nested program, is the failure mode.
const MUTATING = /^(?:tee|mv|cp|rm|rmdir|touch|chmod|chown|ln|install|dd|truncate|patch|New-Item|Set-Content|Add-Content|Remove-Item|Copy-Item|Move-Item|Set-ItemProperty)$/i;

// program → { w: writes, r: reads }. Verbs not listed resolve to null.
// Exported as data so the invariant tests can enumerate it rather than probe it
// with a guessed verb list — the first attempt did, and reported a defect in
// `cargo` that was really a gap in the probe.
export const SUBCOMMANDS = {
  git: {
    w: ["add", "commit", "push", "merge", "rebase", "reset", "checkout", "switch", "restore",
        "cherry-pick", "revert", "stash", "clean", "apply", "am", "pull", "clone", "init", "gc", "prune"],
    r: ["log", "show", "diff", "status", "blame", "describe", "shortlog", "reflog", "grep",
        "ls-files", "ls-tree", "rev-parse", "rev-list", "cat-file", "for-each-ref", "whatchanged"],
  },
  docker: {
    w: ["run", "rm", "rmi", "build", "push", "pull", "create", "start", "stop", "restart", "kill",
        "commit", "tag", "prune", "cp", "load", "import"],
    r: ["ps", "images", "logs", "inspect", "stats", "top", "port", "version", "info", "history", "events"],
  },
  kubectl: {
    w: ["apply", "create", "delete", "patch", "replace", "scale", "edit", "annotate", "label",
        "set", "rollout", "drain", "cordon", "uncordon", "taint", "expose", "run", "cp"],
    r: ["get", "describe", "logs", "top", "explain", "api-resources", "api-versions", "version",
        "cluster-info", "events", "diff", "auth"],
  },
  helm: {
    w: ["install", "upgrade", "uninstall", "delete", "rollback", "push", "create"],
    r: ["list", "get", "status", "show", "history", "template", "lint", "search", "version"],
  },
  terraform: {
    w: ["apply", "destroy", "import", "taint", "untaint", "init"],
    r: ["plan", "show", "output", "validate", "providers", "version", "graph", "console"],
  },
  npm: {
    w: ["install", "i", "ci", "add", "remove", "uninstall", "update", "upgrade", "publish",
        "link", "prune", "dedupe", "init", "version"],
    r: ["ls", "list", "view", "info", "outdated", "audit", "why", "search", "ping"],
  },
  cargo: {
    // `build`/`test`/`clippy` write to target/, which is sandbox scratch, not a
    // consequence in the world — the distinction this flag exists to make.
    w: ["install", "uninstall", "publish", "add", "remove", "update", "clean", "fix", "new",
        "init", "vendor", "generate-lockfile"],
    r: ["build", "test", "check", "clippy", "fmt", "doc", "bench", "tree", "metadata", "search"],
  },
  pip: {
    w: ["install", "uninstall", "download"],
    r: ["list", "show", "freeze", "check", "search"],
  },
  apt: {
    w: ["install", "remove", "purge", "upgrade", "dist-upgrade", "autoremove", "update"],
    r: ["list", "show", "search", "policy", "depends", "rdepends"],
  },
  brew: {
    w: ["install", "uninstall", "upgrade", "remove", "link", "unlink", "tap", "untap", "cleanup", "reinstall"],
    r: ["list", "info", "search", "outdated", "deps", "config", "doctor"],
  },
  systemctl: {
    w: ["start", "stop", "restart", "reload", "enable", "disable", "mask", "unmask", "daemon-reload", "kill"],
    r: ["status", "list-units", "list-unit-files", "is-active", "is-enabled", "show", "cat"],
  },
  sc: { w: ["create", "delete", "start", "stop", "config", "failure"], r: ["query", "qc", "queryex"] },
  reg: { w: ["add", "delete", "import", "copy", "restore", "load", "unload"], r: ["query", "export", "compare"] },
};
// Same grammar, same verbs.
SUBCOMMANDS.podman = SUBCOMMANDS.docker;
SUBCOMMANDS.tofu = SUBCOMMANDS.terraform;
SUBCOMMANDS.pnpm = SUBCOMMANDS.npm;
SUBCOMMANDS.yarn = SUBCOMMANDS.npm;
SUBCOMMANDS.pip3 = SUBCOMMANDS.pip;

export const SUB_LOOKUP = new Map(
  Object.entries(SUBCOMMANDS).map(([prog, { w, r }]) => [
    prog,
    { w: new Set(w), r: new Set(r) },
  ]),
);

// Global flags that take a separate value, across the programs above. Kept
// small: a wrong guess here only shifts which token is read as the verb, and an
// unrecognised verb is null rather than a fabricated answer.
const GLOBAL_FLAGS_WITH_VALUE = /^(?:-C|-n|-c|-f|-o|-s|--namespace|--context|--chdir|--config|--file|--output|--kubeconfig|--cluster|--user|--manifest-path|--prefix)$/i;

/**
 * Consequence of a subcommand-bearing program, or undefined when the program is
 * not one. `args` are the tokens after the program.
 *
 * @returns {true|false|null|undefined} null = recognised program, undecidable verb
 */
export function subcommandConsequence(program, args) {
  const table = SUB_LOOKUP.get(String(program || "").toLowerCase());
  if (!table) return undefined;
  // The verb is the first token that is not a flag or a flag's value. Global
  // flags come before it (`git -C dir status`, `kubectl -n prod get pods`), and
  // a long flag can carry its value separately.
  for (let i = 0; i < args.length; i++) {
    const tok = String(args[i] || "");
    if (!tok) continue;
    if (tok.startsWith("-")) {
      // `-C dir`, `-n prod`, `--namespace prod` consume the next token; the
      // `=` forms and boolean flags do not.
      if (!tok.includes("=") && /^-{1,2}[A-Za-z][\w-]*$/.test(tok) && GLOBAL_FLAGS_WITH_VALUE.test(tok)) i += 1;
      continue;
    }
    const verb = tok.toLowerCase();
    if (table.w.has(verb)) return true;
    if (table.r.has(verb)) return false;
    return null;
  }
  return null;
}

// Programs that CANNOT write without shell redirection. Membership is the test —
// not "usually reads", which is how a proxy list rots. `find` is absent because
// `-delete` and `-exec rm` exist; `awk` because `print > f` does; `xargs`, `env`
// and every interpreter because they run whatever they are handed. `sed` IS here:
// its in-place flag is caught above this list, and treating a bare `sed` as a
// read is a decision this file already made and documented.
//
// An unrecognised program — a house CLI, a built binary, a script — is null, not
// false. That was the last unearned claim left in this function: the disclosure
// that `price-sync --once` reads as read-only lived in a PR description, where no
// reader of the panel would ever meet it.
const READ_ONLY = /^(?:grep|egrep|fgrep|rg|ag|ack|cat|head|tail|less|more|wc|sort|uniq|cut|tr|jq|yq|sed|diff|cmp|file|stat|du|df|ls|dir|tree|pwd|basename|dirname|realpath|readlink|date|printenv|whoami|id|hostname|uname|ps|pgrep|lsof|netstat|ss|xxd|od|strings|nm|objdump|readelf|md5sum|sha1sum|sha256sum|column|seq|nproc|free|uptime|man|whereis|Get-Content|Get-ChildItem|Get-Process|Select-String|Measure-Object)$/i;

/**
 * One statement's consequence: true / false / null.
 *
 * Order matters. A redirection or an in-place edit is a write whatever the
 * program, so those win over a subcommand that reads — `git show x > f.txt`
 * writes a file even though `git show` reads.
 */
export function statementMutates(program, args, statement) {
  if (MUTATING_FLAG_RE.test(statement)) return true;
  if (/(?:^|\s)>{1,2}(?:\s|$)/.test(statement)) return true;
  if (MUTATING.test(program)) return true;
  const sub = subcommandConsequence(program, args);
  if (sub !== undefined) return sub;
  if (READ_ONLY.test(program)) return false;
  return null;
}

/**
 * Fold statement consequences into one for the whole command.
 *
 * A known write anywhere wins — the command as a whole changed something. Short
 * of that, an undecidable statement makes the whole thing undecidable, because
 * reporting `false` would claim read-only on a command containing a verb nobody
 * classified.
 */
export function foldMutates(values) {
  if (values.some((v) => v === true)) return true;
  if (values.some((v) => v === null)) return null;
  return false;
}

const MUTATING_FLAG_RE = /(?:^|\s)(?:sed|perl|ruby)\s+(?:-\w*\s+)*-\w*i\b/i;
const EGRESS = /^(?:curl|wget|ssh|sshpass|scp|sftp|rsync|nc|ncat|telnet|aws|az|gcloud|gh|glab|tea|fj|Invoke-WebRequest|Invoke-RestMethod)$/i;

// Placeholder vocabulary. Kept coarse on purpose: the more the placeholder
// preserves, the finer the clusters, and fine clusters are the failure mode
// (one per input file). Extensions ARE preserved on paths — `.dmp` vs `.log`
// is a real difference in kind, not a difference in value.
const PLACEHOLDERS = [
  [/^(['"]?)https?:\/\/\S+\1$/i, "<URL>"],
  [/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i, "<UUID>"],
  [/^(?:0x)?[0-9a-f]{7,}$/i, "<HEX>"],
  [/^[-+]?\d+(?:\.\d+)?$/, "<NUM>"],
  [/^(?:\$\{?\w|%\w+%|\$env:)/i, "<VAR>"],
];

const EXT_RE = /\.([A-Za-z0-9]{1,6})$/;

function placeholderFor(token) {
  for (const [re, tag] of PLACEHOLDERS) if (re.test(token)) return tag;
  // A path keeps its extension, which carries kind, and loses its location,
  // which carries only which machine ran it.
  if (/[\\/]/.test(token) || EXT_RE.test(token)) {
    const ext = EXT_RE.exec(token);
    return ext ? `<PATH${ext[1].toLowerCase() === "exe" ? ":exe" : `.${ext[1].toLowerCase()}`}>` : "<PATH>";
  }
  return null;
}

// `aws s3 cp` is the deepest real subcommand nesting in this corpus; beyond two
// the tokens are arguments.
const SUBCOMMAND_DEPTH = 2;

const isFlag = (t) => /^-{1,2}[A-Za-z?]/.test(t) || /^\/[A-Za-z]{1,12}$/.test(t);

// A flag's own name is structure; its value is not. `--out x.json` and
// `--out y.json` are the same act.
function normalizeFlag(token) {
  const eq = token.indexOf("=");
  return eq > 0 ? `${token.slice(0, eq)}=<VAL>` : token;
}

// How MANY times a flag repeats is a value, not structure. `cargo clippy -p a`
// and `cargo clippy -p a -p b -p c` are one behaviour applied to more packages
// — the package count is scope, and scope is a value. On the real corpus this
// single distinction forked the local verification loop into five clusters that
// were plainly one thing. Repeats are dropped rather than marked, so the
// one-package and many-package forms unify.
function collapseRepeatedFlags(parts) {
  const out = [];
  const firstAt = new Map();
  for (let i = 0; i < parts.length; i += 1) {
    const t = parts[i];
    if (!isFlag(t)) { out.push(t); continue; }
    const takesValue = i + 1 < parts.length && !isFlag(parts[i + 1]);
    if (firstAt.has(t)) {
      if (takesValue) i += 1;
      continue;
    }
    firstAt.set(t, out.length);
    out.push(t);
  }
  return out;
}

// Redaction. Mining reads RAW commands, so filesystem paths carry usernames and
// project layouts. That is fine on a laptop against your own corpus and not
// fine for an agent running server-side over someone else's traces, where the
// judge payload would otherwise ship operator identities to a model. Applied at
// the payload boundary rather than during skeletonization, because the miner
// still needs the real text to parse.
const HOME_RE = /(?:\/(?:Users|home)\/|[A-Za-z]:\\Users\\)([^\/\\\s'"]+)/g;

export function redactPaths(text) {
  return String(text ?? "").replace(HOME_RE, (whole) => (whole.includes("\\") ? "C:\\Users\\<user>" : "/home/<user>"));
}

// The program a statement runs, with its path and platform suffix removed so
// `/c/Program Files/…/cdb.exe`, `C:\Kits\cdb.exe` and `cdb` are one program.
export function programOf(token) {
  const bare = String(token || "").split(/[\\/]/).pop() || "";
  return bare.replace(/\.(?:exe|com|bat|cmd|sh|ps1)$/i, "").toLowerCase();
}

// ── Embedded scripts ────────────────────────────────────────────────────────

const HEREDOC_RE = /<<-?\s*(['"]?)([A-Za-z_]\w*)\1\s*\n([\s\S]*?)\n?^\2\s*$/m;
const INLINE_RE = /\s-(?:c|e)\s+(['"])([\s\S]*?)\1/;

const LANGS = [
  ["python", /^(?:python3?(?:\.\d+)?|py|uv run|poetry run)$/i],
  ["node", /^(?:node|bun|deno|tsx|ts-node)$/i],
  ["shell", /^(?:bash|sh|zsh|dash|pwsh|powershell)$/i],
];

function languageOf(program) {
  for (const [lang, re] of LANGS) if (re.test(program)) return lang;
  return null;
}

/**
 * The source an interpreter was handed inline, if any. Heredocs first: they are
 * how anything longer than a line gets written, and a `-c` match inside a
 * heredoc body would capture the wrong span.
 */
export function embeddedSource(statement) {
  const here = HEREDOC_RE.exec(statement);
  if (here) return here[3];
  const inline = INLINE_RE.exec(statement);
  return inline ? inline[2] : null;
}

const PY_IMPORT_RE = /^\s*(?:from\s+([A-Za-z_][\w.]*)|import\s+([A-Za-z_][\w.,\s]*))/gm;
const CALL_RE = /\b([A-Za-z_][\w.]{1,40})\s*\(/g;
// Language builtins say "this is Python", not "this is a behaviour". Keeping
// them would make every script's call set 80% identical and collapse unrelated
// work into one cluster.
const CALL_NOISE = new Set([
  "print", "len", "str", "int", "float", "list", "dict", "set", "tuple", "range",
  "enumerate", "zip", "map", "filter", "sorted", "sum", "min", "max", "abs",
  "open", "type", "isinstance", "getattr", "setattr", "hasattr", "format",
  "join", "split", "strip", "replace", "append", "extend", "get", "items",
  "keys", "values", "sub", "match", "search", "findall", "group", "groups",
  "if", "for", "while", "return", "with", "def", "class", "lambda", "and", "or",
  "not", "in", "is", "console.log", "require", "function", "await", "async",
  "String", "Number", "Object", "Array", "JSON.stringify", "JSON.parse",
]);

const CAP = 12;

/**
 * An embedded script's skeleton: the API surface it touches. Modules it pulls
 * in plus the qualified calls it makes, both sorted so ordering differences do
 * not fork the cluster.
 */
export function skeletonizeScript(source, lang = "python") {
  const src = String(source || "");
  const mods = new Set();
  // Alias → module. `import pandas as pd` makes `pd.read_csv` and
  // `pandas.read_csv` the same call; without resolving it they fork.
  const alias = new Map();

  if (lang === "python") {
    PY_IMPORT_RE.lastIndex = 0;
    for (let m = PY_IMPORT_RE.exec(src); m; m = PY_IMPORT_RE.exec(src)) {
      for (const part of (m[1] || m[2] || "").split(",")) {
        const spec = part.trim();
        if (!spec) continue;
        const as = /^([\w.]+)\s+as\s+(\w+)$/.exec(spec);
        const root = (as ? as[1] : spec).split(/[.\s]/)[0];
        if (!root) continue;
        mods.add(root);
        if (as) alias.set(as[2], root);
      }
    }
    // `from pandas import read_csv` binds a bare name to a module surface.
    for (const m of src.matchAll(/^\s*from\s+([\w.]+)\s+import\s+([\w,\s*]+)/gm)) {
      const root = m[1].split(".")[0];
      for (const name of m[2].split(",")) {
        const n = name.trim().split(/\s+as\s+/).pop();
        if (n && n !== "*") alias.set(n, root);
      }
    }
  } else {
    for (const m of src.matchAll(/(?:require\(|from\s+)['"]([^'"]+)['"]/g)) {
      mods.add(m[1].split("/")[0].replace(/^node:/, ""));
    }
    for (const m of src.matchAll(/(?:const|let|var)\s+(\w+)\s*=\s*require\(['"]([^'"]+)['"]/g)) {
      alias.set(m[1], m[2].split("/")[0].replace(/^node:/, ""));
    }
    for (const m of src.matchAll(/import\s+(?:\*\s+as\s+)?(\w+)\s+from\s+['"]([^'"]+)['"]/g)) {
      alias.set(m[1], m[2].split("/")[0].replace(/^node:/, ""));
    }
  }

  const calls = new Set();
  CALL_RE.lastIndex = 0;
  for (let m = CALL_RE.exec(src); m; m = CALL_RE.exec(src)) {
    const name = m[1];
    const tail = name.split(".").pop();
    if (CALL_NOISE.has(name) || CALL_NOISE.has(tail)) continue;
    if (name.includes(".")) {
      const [receiver, ...restParts] = name.split(".");
      const mod = alias.get(receiver) || (mods.has(receiver) ? receiver : null);
      // A receiver that is not an imported module is a LOCAL VARIABLE, and its
      // name is per-script noise: `df.groupby` and `frame.groupby` are the same
      // call. Keep the method, drop the variable.
      calls.add(mod ? `${mod}.${restParts.join(".")}` : `.${restParts.join(".")}`);
      continue;
    }
    const mod = alias.get(name);
    if (mod) { calls.add(`${mod}.${name}`); continue; }
    // An unqualified lowercase name with no import behind it is a local helper
    // — it names this script, not a shared behaviour.
    if (!/^[A-Z]/.test(name) && !mods.has(name)) continue;
    calls.add(name);
  }

  const parts = [];
  if (mods.size) parts.push(`import{${[...mods].sort().slice(0, CAP).join(",")}}`);
  if (calls.size) parts.push(`call{${[...calls].sort().slice(0, CAP).join(",")}}`);
  // A script with no imports and no API calls is a one-off expression. Saying
  // so is more honest than inventing a key it would share with every other
  // one-off.
  return parts.length ? `${lang}: ${parts.join(" ")}` : `${lang}: <inline expression>`;
}

// ── Shell ───────────────────────────────────────────────────────────────────

// A slot value longer than this is kept as a labelled head, not dropped: the
// interesting part of a grep pattern or a URL is almost always its front, and
// clusters hold these per distinct value, so the cap is a memory bound.
const SLOT_VALUE_MAX = 120;

const slotValue = (t) => (t.length > SLOT_VALUE_MAX ? `${t.slice(0, SLOT_VALUE_MAX - 1)}…` : t);

// An embedded script's slot value: the collapsed source, hash-suffixed when it
// had to be truncated. The suffix is not decoration — two long sources sharing
// a 120-char prefix would otherwise COUNT as one distinct value, and an
// under-counted spread is evidence in the over-claiming direction, which is the
// direction this whole profile exists to push against.
function srcSlotValue(src) {
  const collapsed = String(src).replace(/\s+/g, " ").trim();
  if (collapsed.length <= SLOT_VALUE_MAX) return collapsed;
  return `${collapsed.slice(0, SLOT_VALUE_MAX - 10)}…⟨${skeletonKey(collapsed).slice(0, 6)}⟩`;
}

function skeletonizeStatement(statement) {
  const toks = tokenize(statement);
  if (!toks.length) return null;

  // Leading `VAR=value` and PowerShell's call operator are invocation syntax,
  // not part of the program's shape.
  let i = 0;
  while (i < toks.length && (/^\$?\w+=/.test(toks[i]) || toks[i] === "&" || toks[i] === "sudo" || toks[i] === "time")) i += 1;
  if (i >= toks.length) return null;

  const program = programOf(toks[i]);
  if (!program) return null;
  const scaffold = SCAFFOLD_VERBS.has(program);

  const lang = languageOf(program);
  if (lang) {
    const src = embeddedSource(statement);
    if (src) {
      return {
        program,
        scaffold: false,
        // An interpreter handed a script IS the script — attributing it to
        // `python3` is the exact loss of signal this module exists to prevent.
        skeleton: lang === "shell" ? skeletonizeShell(src).skeleton : skeletonizeScript(src, lang),
        embedded: lang,
        // A script's variability lives in its SOURCE, not in token slots — the
        // API-surface skeleton has no placeholders to profile, which left the
        // slot evidence empty exactly where claims most need it (a judge read
        // "edits a script" off five heredocs and nothing measured whether the
        // other forty differed). The whole source is one slot value, so the
        // profile can say "9 calls, 7 distinct scripts" the same way it says it
        // for a grep pattern.
        values: [{ ph: `<${lang} src>`, value: srcSlotValue(src) }],
        mutates: /\bopen\s*\([^)]*['"][wa]/.test(src) || /\b(?:subprocess|os\.system|shutil|boto3)\b/.test(src),
        egress: /\b(?:requests|urllib|httpx|aiohttp|socket|boto3|fetch)\b/.test(src),
      };
    }
  }

  const parts = [program];
  // What each placeholder REPLACED, keyed by the placeholder itself. The
  // skeleton deliberately throws values away to make instances collide — but
  // the values are the evidence for how much one skeleton actually varies, so
  // they are kept beside it rather than lost. `grep -n <ARG> <PATH.md>` with a
  // single recurring <ARG> and with forty distinct ones are the same shape and
  // very different claims.
  const values = [];
  // A bare word is either a SUBCOMMAND (`git commit`, `aws s3 cp`, `cargo
  // build`) or a VALUE (a grep pattern, a branch name, an image tag) — and the
  // two are lexically identical. Position separates them: subcommands come
  // before any flag, values come after. Without this, `rg 'foo'` and
  // `rg 'bar'` are different behaviours, which is the same value-as-structure
  // mistake that placeholders exist to prevent.
  let subcommandSlots = SUBCOMMAND_DEPTH;
  let sawFlag = false;
  for (let j = i + 1; j < toks.length; j += 1) {
    const t = toks[j];
    if (isFlag(t)) {
      sawFlag = true;
      const norm = normalizeFlag(t);
      if (norm.endsWith("=<VAL>")) values.push({ ph: norm, value: slotValue(t.slice(t.indexOf("=") + 1)) });
      parts.push(norm);
      continue;
    }
    const ph = placeholderFor(t);
    if (ph) { parts.push(ph); values.push({ ph, value: slotValue(t) }); continue; }
    const isSubcommand = !sawFlag && subcommandSlots > 0 && /^[a-z][\w-]{0,20}$/i.test(t);
    if (isSubcommand) { subcommandSlots -= 1; parts.push(t.toLowerCase()); continue; }
    parts.push("<ARG>");
    values.push({ ph: "<ARG>", value: slotValue(t) });
  }
  return {
    program,
    scaffold,
    skeleton: collapseRepeatedFlags(parts).join(" "),
    embedded: null,
    values,
    mutates: statementMutates(program, toks.slice(i + 1), statement),
    egress: EGRESS.test(program),
  };
}

/**
 * A whole shell command's skeleton.
 *
 * @returns {{skeleton: string, core: string, programs: string[],
 *            statements: object[], slotValues: object[],
 *            mutates: true|false|null, egress: boolean}}
 *   `skeleton` keeps every statement; `core` drops the scaffolding ones and is
 *   the clustering key, so one behaviour does not fork into a cluster per
 *   working directory. `slotValues` are the {ph, value} pairs the CORE's
 *   placeholders replaced — the values the key deliberately forgot, kept so a
 *   cluster can say how much its one shape actually varies.
 */
export function skeletonizeShell(command) {
  const resolved = resolveShellVars(String(command || ""));
  const statements = [];
  for (const st of splitStatements(resolved)) {
    const s = skeletonizeStatement(st.text.trim());
    if (s) statements.push(s);
  }
  const real = statements.filter((s) => !s.scaffold);
  const shape = (list) => list.map((s) => s.skeleton).join(" | ");
  const core = real.length ? real : statements;
  return {
    skeleton: shape(statements),
    // All-scaffolding commands are real (`cd x && ls`) and keep their own
    // shape rather than collapsing to an empty key.
    core: shape(core),
    programs: [...new Set(core.map((s) => s.program))],
    statements,
    slotValues: core.flatMap((s) => s.values || []),
    mutates: foldMutates(statements.map((s) => s.mutates)),
    egress: statements.some((s) => s.egress),
  };
}

// Stable across runs and processes — cluster identity depends on it, so it
// cannot be a hash seeded by anything ambient.
export function skeletonKey(core) {
  let h1 = 0x811c9dc5;
  let h2 = 0x01000193;
  const s = String(core || "");
  for (let i = 0; i < s.length; i += 1) {
    h1 = ((h1 ^ s.charCodeAt(i)) * 0x01000193) >>> 0;
    h2 = ((h2 + s.charCodeAt(i)) * 0x85ebca6b) >>> 0;
  }
  return `${h1.toString(36)}${h2.toString(36)}`;
}
